import { apiClient } from '@api/client';
import type { CurrentSubscription, Plan } from '@/types/subscriptions';
import type {
  CRMAccountBilling,
  CRMAccountTenantListResponse,
  CRMAccountTenantUserListResponse,
} from '@/types/api';
import type {
  PromoteAccountRequest,
  PromoteAccountResponse,
  Promotion,
  PromotionCreatePayload,
  PromotionListResponse,
  ResendInviteResponse,
  SubscriptionOverridePayload,
} from '@/types/promote';

export async function promoteAccount(accountId: string, body: PromoteAccountRequest) {
  const { data } = await apiClient.post<PromoteAccountResponse>(
    `/crm/accounts/${accountId}/promote`,
    body,
  );
  return data;
}

export async function createAccountTenant(accountId: string, body: PromoteAccountRequest) {
  const { data } = await apiClient.post<PromoteAccountResponse>(
    `/crm/accounts/${accountId}/tenants`,
    body,
  );
  return data;
}

export async function promoteLead(leadId: string, body: PromoteAccountRequest) {
  const { data } = await apiClient.post<PromoteAccountResponse>(
    `/crm/leads/${leadId}/promote`,
    body,
  );
  return data;
}

export async function resendInvite(accountId: string, userId?: string) {
  const { data } = await apiClient.post<ResendInviteResponse>(
    `/crm/accounts/${accountId}/invites/resend`,
    { user_id: userId },
  );
  return data;
}

export async function resendTenantInvite(accountId: string, tenantId: string, userId?: string) {
  const { data } = await apiClient.post<ResendInviteResponse>(
    `/crm/accounts/${accountId}/tenants/${tenantId}/invites/resend`,
    { user_id: userId },
  );
  return data;
}

export async function updateSubscriptionOverride(
  accountId: string,
  body: SubscriptionOverridePayload,
) {
  const { data } = await apiClient.post<{
    ok: boolean;
    subscription_id: string;
    stripe_synced: boolean;
    stripe_action?: string | null;
  }>(
    `/crm/accounts/${accountId}/subscription/override`,
    body,
  );
  return data;
}

export async function updateTenantSubscriptionAllocation(
  accountId: string,
  tenantId: string,
  body: SubscriptionOverridePayload,
) {
  const { data } = await apiClient.post<CurrentSubscription>(
    `/crm/accounts/${accountId}/tenants/${tenantId}/subscription/allocation`,
    body,
  );
  return data;
}

export async function fetchTenantSubscription(accountId: string) {
  const { data } = await apiClient.get<CurrentSubscription>(
    `/crm/accounts/${accountId}/tenant-subscription`,
  );
  return data;
}

export async function fetchAccountTenants(accountId: string) {
  const { data } = await apiClient.get<CRMAccountTenantListResponse>(
    `/crm/accounts/${accountId}/tenants`,
  );
  return data;
}

export async function fetchAccountTenantUsers(accountId: string) {
  const { data } = await apiClient.get<CRMAccountTenantUserListResponse>(
    `/crm/accounts/${accountId}/tenant-users`,
  );
  return data;
}

export async function fetchAccountTenantSubscription(accountId: string, tenantId: string) {
  const { data } = await apiClient.get<CurrentSubscription>(
    `/crm/accounts/${accountId}/tenants/${tenantId}/subscription`,
  );
  return data;
}

export async function makeAccountTenantPrimary(accountId: string, tenantId: string) {
  const { data } = await apiClient.post(
    `/crm/accounts/${accountId}/tenants/${tenantId}/make-primary`,
  );
  return data;
}

export async function fetchAccountBilling(accountId: string) {
  const { data } = await apiClient.get<CRMAccountBilling>(`/crm/accounts/${accountId}/billing`);
  return data;
}

export async function fetchSubscriptionPlans() {
  const { data } = await apiClient.get<{ plans: Plan[] }>('/subscriptions/plans');
  return data.plans;
}

export async function fetchPromotions(filters?: { isActive?: boolean; kind?: string }) {
  const { data } = await apiClient.get<PromotionListResponse>('/crm/promotions', {
    params: {
      is_active: filters?.isActive,
      kind: filters?.kind,
    },
  });
  return data.promotions;
}

export async function createPromotion(body: PromotionCreatePayload) {
  const { data } = await apiClient.post<Promotion>('/crm/promotions', body);
  return data;
}

export async function archivePromotion(promotionId: string) {
  const { data } = await apiClient.post<Promotion>(`/crm/promotions/${promotionId}/archive`);
  return data;
}
